import React, { useId, useState } from 'react';
import { Building2, ChevronDown, ShieldCheck, UserRound } from 'lucide-react';
import type { PublicCompanyDetails, UserAccountType } from '../services/api.types';
import { useTranslations } from '../translations';
import './AccountTypeInfo.css';

interface AccountTypeInfoProps {
  accountType?: UserAccountType | null;
  companyDetails?: PublicCompanyDetails;
}

type OpenSection = 'account' | 'rights' | null;

const AccountTypeInfo: React.FC<AccountTypeInfoProps> = ({ accountType, companyDetails }) => {
  const t = useTranslations();
  const [openSection, setOpenSection] = useState<OpenSection>(null);
  const accountButtonId = useId();
  const accountPanelId = useId();
  const rightsButtonId = useId();
  const rightsPanelId = useId();

  if (!accountType) return null;

  const isCompany = accountType === 'COMPANY';
  const label = isCompany ? t.accountType.companyPublicLabel : t.accountType.privatePublicLabel;
  const accountTitle = isCompany
    ? t.accountType.accountDetailsCompanyTitle
    : t.accountType.accountDetailsPrivateTitle;
  const description = isCompany
    ? t.accountType.companyPublicDescription
    : t.accountType.privatePublicDescription;
  const rightsSummary = isCompany
    ? t.accountType.consumerRightsCompanySummary
    : t.accountType.consumerRightsPrivateSummary;
  const TypeIcon = isCompany ? Building2 : UserRound;

  const toggleSection = (section: Exclude<OpenSection, null>) => {
    setOpenSection((current) => (current === section ? null : section));
  };

  return (
    <div className="account-type-info">
      <section className={`account-type-accordion ${openSection === 'account' ? 'is-open' : ''}`}>
        <button
          id={accountButtonId}
          type="button"
          className="account-type-accordion-trigger"
          aria-expanded={openSection === 'account'}
          aria-controls={accountPanelId}
          onClick={() => toggleSection('account')}
        >
          <span className="account-type-accordion-icon" aria-hidden="true">
            <TypeIcon size={18} />
          </span>
          <span className="account-type-accordion-heading">
            <strong>{accountTitle}</strong>
            <small>{label}</small>
          </span>
          <ChevronDown className="account-type-accordion-chevron" size={18} aria-hidden="true" />
        </button>

        <div
          id={accountPanelId}
          className="account-type-accordion-panel"
          role="region"
          aria-labelledby={accountButtonId}
          hidden={openSection !== 'account'}
        >
          <p>{description}</p>
          {isCompany && companyDetails?.companyLegalName && (
            <dl className="account-type-company-summary">
              <div><dt>{t.accountType.legalName}</dt><dd>{companyDetails.companyLegalName}</dd></div>
              {companyDetails.companyLegalForm && <div><dt>{t.accountType.legalForm}</dt><dd>{companyDetails.companyLegalForm}</dd></div>}
              {companyDetails.taxIdentificationNumber && <div><dt>{t.accountType.taxId}</dt><dd>{companyDetails.taxIdentificationNumber}</dd></div>}
              {companyDetails.tradeRegisterNumber && <div><dt>{t.accountType.tradeRegisterNumber}</dt><dd>{companyDetails.tradeRegisterNumber}</dd></div>}
              {companyDetails.registeredOffice && <div><dt>{t.accountType.registeredOffice}</dt><dd>{companyDetails.registeredOffice}</dd></div>}
              {companyDetails.companyEmail && <div><dt>{t.accountType.companyEmail}</dt><dd>{companyDetails.companyEmail}</dd></div>}
              {companyDetails.companyPhone && <div><dt>{t.accountType.companyPhone}</dt><dd>{companyDetails.companyPhone}</dd></div>}
              {companyDetails.vatRegistered !== null && companyDetails.vatRegistered !== undefined && (
                <div>
                  <dt>{t.accountType.vatRegistered}</dt>
                  <dd>{companyDetails.vatRegistered ? t.accountType.vatPayer : t.accountType.nonVatPayer}</dd>
                </div>
              )}
            </dl>
          )}
        </div>
      </section>

      <section className={`account-type-accordion ${openSection === 'rights' ? 'is-open' : ''}`}>
        <button
          id={rightsButtonId}
          type="button"
          className="account-type-accordion-trigger"
          aria-expanded={openSection === 'rights'}
          aria-controls={rightsPanelId}
          onClick={() => toggleSection('rights')}
        >
          <span className="account-type-accordion-icon" aria-hidden="true">
            <ShieldCheck size={18} />
          </span>
          <span className="account-type-accordion-heading">
            <strong>{t.accountType.consumerRightsTitle}</strong>
            <small>{rightsSummary}</small>
          </span>
          <ChevronDown className="account-type-accordion-chevron" size={18} aria-hidden="true" />
        </button>

        <div
          id={rightsPanelId}
          className="account-type-accordion-panel account-type-rights-panel"
          role="region"
          aria-labelledby={rightsButtonId}
          hidden={openSection !== 'rights'}
        >
          <p>
            {isCompany
              ? t.accountType.consumerRightsCompanyIntro
              : t.accountType.consumerRightsPrivateIntro}
          </p>
          {isCompany && <p>{t.accountType.consumerRightsCompanyWithdrawal}</p>}
          <h4>{t.accountType.consumerResponsibilitiesTitle}</h4>
          <ul>
            <li>{t.accountType.platformResponsibility}</li>
            <li>
              {isCompany
                ? t.accountType.companySellerResponsibility
                : t.accountType.privateSellerResponsibility}
            </li>
            <li>{t.accountType.buyerResponsibility}</li>
          </ul>
          <p>
            {isCompany
              ? t.accountType.companyConsumerAction
              : t.accountType.privateConsumerAction}
          </p>
          <small className="account-type-rights-disclaimer">
            {t.accountType.consumerRightsDisclaimer}
          </small>
        </div>
      </section>
    </div>
  );
};

export default AccountTypeInfo;
